/**
 * PaymentsDesktop — десктоп-раскладка «Платежей»: sticky-шапка (+экспорт),
 * сводка за период, тулбар (поиск + статус + период) и таблица журнала.
 * Данные/фильтры/детали/возврат держит оркестратор Payments.
 */

import {Download} from 'lucide-react';

import type {PaymentRow, PaymentsBundle, PaymentStatus} from '@services/payments';
import {PeriodField, type Range} from '@components/PeriodPicker';
import {SearchField} from '@components/SearchField';
import {Button} from '@components/ui/Button';
import {Loader} from '@components/ui/Loader';
import {FilterSelect} from '@pages/Clients/FilterControls';

import {STATUS_FILTER_OPTIONS} from '../Payments.utils';

import {PaymentsSummary} from './PaymentsSummary';
import {PaymentsTable} from './PaymentsTable';

type PaymentsDesktopProps = {
  data?: PaymentsBundle;
  isLoading: boolean;
  rows: PaymentRow[];
  search: string;
  onSearch: (v: string) => void;
  status: PaymentStatus | null;
  onStatus: (v: PaymentStatus | null) => void;
  period: Range;
  periodLabel: string;
  onPeriod: (p: Range) => void;
  onDetail: (row: PaymentRow) => void;
  onOpenClient: (row: PaymentRow) => void;
  onRefund: (row: PaymentRow) => void;
  onSoon: (text: string) => void;
};

export const PaymentsDesktop = ({
  data,
  isLoading,
  rows,
  search,
  onSearch,
  status,
  onStatus,
  period,
  periodLabel,
  onPeriod,
  onDetail,
  onOpenClient,
  onRefund,
  onSoon,
}: PaymentsDesktopProps) => (
  <>
    <header className="sticky top-0 z-10 flex items-center gap-4 border-b border-skr-line bg-background/80 px-[30px] py-[18px] backdrop-blur">
      <div className="min-w-0 flex-1">
        <h1 className="text-[21px] font-bold -tracking-[.02em]">Платежи</h1>
        <div className="mt-0.5 text-[13px] text-muted-foreground">
          Журнал всех транзакций · для сверки и бухгалтерии
        </div>
      </div>
      <Button variant="outline" onClick={() => onSoon('Экспорт CSV / Excel — скоро')}>
        <Download />
        Экспорт CSV / Excel
      </Button>
    </header>

    <div className="flex flex-col gap-[18px] px-[30px] pb-[50px] pt-6">
      {isLoading || !data ? (
        <div className="flex min-h-[440px] items-center justify-center">
          <Loader label="Загружаем платежи…" />
        </div>
      ) : (
        <>
          <div className="text-[13px] text-muted-foreground">
            Итоги за период · <span className="font-medium text-skr-ink">{periodLabel}</span>
          </div>
          <PaymentsSummary summary={data.summary} />

          <div className="mt-1 flex flex-wrap items-center gap-2.5">
            <SearchField
              value={search}
              onChange={onSearch}
              placeholder="Поиск по клиенту…"
              className="h-10 min-w-[240px] max-w-[340px] flex-1 rounded-[10px] border border-input bg-card px-[13px]"
            />
            <div className="flex-1" />
            <FilterSelect
              label="Статус"
              options={STATUS_FILTER_OPTIONS}
              value={status}
              onChange={(v) => onStatus((v as PaymentStatus) ?? null)}
            />
            <PeriodField value={period} onChange={onPeriod} />
          </div>

          <PaymentsTable
            rows={rows}
            totalCount={data.totalCount}
            onDetail={onDetail}
            onOpenClient={onOpenClient}
            onRefund={onRefund}
          />
        </>
      )}
    </div>
  </>
);
